import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { FilterBar } from "@/components/FilterBar";
import { PropertyCard } from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { api, COUNTRY_FLAGS, COUNTRY_LABELS } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { ArrowRight, Sparkles, ShieldCheck, MapPinned, Users } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ total_listings: 0, by_country: {}, users: 0 });
  const [activeCountry, setActiveCountry] = useState("ALL");

  useEffect(() => {
    (async () => {
      const params = activeCountry !== "ALL" ? { country: activeCountry } : {};
      const [{ data: items }, { data: s }] = await Promise.all([
        api.get("/listings", { params: { ...params, limit: 6 } }),
        api.get("/stats"),
      ]);
      setFeatured(items);
      setStats(s);
    })();
  }, [activeCountry]);

  const handleSearch = (filters) => {
    const params = new URLSearchParams(filters).toString();
    navigate(`/search?${params}`);
  };

  return (
    <Layout>
      <SEO path="/" />
      {/* HERO */}
      <section data-testid={HOME.hero} className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, #0A5C2C 0%, #11783A 40%, #0D3D1F 80%, #0D3D1F 100%)",
          }}
        >
          <img
            src="https://images.unsplash.com/photo-1577047283180-7a25cb2e51df?w=1920&q=85"
            alt=""
            className="w-full h-full object-cover opacity-25 mix-blend-overlay"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FAFAFA] from-70%" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-32 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs tracking-[0.2em] uppercase mb-6 border border-white/20">
              <Sparkles className="w-3 h-3" /> {t("home.badge")}
            </div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white font-medium tracking-tight leading-[1.05] mb-3">
              {t("home.title_part1")}{" "}
              <br />
              {t("home.title_part2")}{" "}
              <br />
              {t("home.title_part3")} <span className="text-[#F4B400]">{t("home.title_highlight")}</span>.
            </h1>
            <div className="font-arabic text-2xl md:text-3xl text-white/80 mb-6" dir="rtl">
              عقارات بلا حدود
            </div>
            <p className="text-white/80 text-lg max-w-xl leading-relaxed mb-8">
              {t("home.description")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate("/search")}
                data-testid={HOME.heroCtaSearch}
                className="rounded-full bg-[#D6202C] hover:bg-[#B5181F] text-white px-7 h-12 shadow-lg"
              >
                {t("home.cta_browse")} <ArrowRight className="w-4 h-4 ms-2" />
              </Button>
              <Button
                onClick={() => navigate("/register")}
                data-testid={HOME.heroCtaPublish}
                variant="outline"
                className="rounded-full bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white hover:text-[#1A1A1A] px-7 h-12"
              >
                {t("home.cta_publish")}
              </Button>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div className="max-w-6xl mx-auto px-6 lg:px-8 -mt-16 relative z-10">
          <FilterBar onSearch={handleSearch} />
        </div>
      </section>

      {/* COUNTRY TABS / STATS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <StatTile icon={<MapPinned />} label={t("home.stat_listings")} value={stats.total_listings} />
          <StatTile icon={<Users />} label={t("home.stat_advertisers")} value={stats.users} />
          <StatTile icon={<ShieldCheck />} label={t("home.stat_verified")} value="100%" />
          <StatTile icon={<Sparkles />} label={t("home.stat_countries")} value="3" />
        </div>

        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">{t("home.featured_overline")}</span>
            <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2">
              {t("home.featured_title")}
            </h2>
          </div>
          <div data-testid={HOME.countryTabs} className="flex gap-2 flex-wrap">
            {[["ALL", t("home.country_all")], ["MA", `${COUNTRY_FLAGS.MA} ${COUNTRY_LABELS.MA}`], ["DZ", `${COUNTRY_FLAGS.DZ} ${COUNTRY_LABELS.DZ}`], ["TN", `${COUNTRY_FLAGS.TN} ${COUNTRY_LABELS.TN}`]].map(([c, label]) => (
              <button
                key={c}
                onClick={() => setActiveCountry(c)}
                data-testid={`country-tab-${c}`}
                className={`px-4 h-10 rounded-full text-sm font-medium transition-all ${
                  activeCountry === c
                    ? "bg-[#1A1A1A] text-white"
                    : "bg-white text-[#1A1A1A] border border-[#E5E5E5] hover:border-[#D6202C]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div data-testid={HOME.featuredGrid} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((l, i) => (
            <PropertyCard key={l.id} listing={l} index={i} />
          ))}
        </div>

        <div className="text-center mt-12">
          <Button
            onClick={() => navigate("/search")}
            variant="outline"
            className="rounded-full h-12 px-7 border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white"
          >
            {t("home.view_all")} <ArrowRight className="w-4 h-4 ms-2" />
          </Button>
        </div>
      </section>

      {/* PROS SECTION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-32 mb-24">
        <div className="rounded-3xl overflow-hidden bg-[#11783A] text-white grid md:grid-cols-2">
          <div className="p-10 md:p-14 flex flex-col justify-center">
            <span className="text-xs tracking-[0.2em] uppercase text-[#F4B400] font-medium">{t("home.pros_overline")}</span>
            <h3 className="font-heading text-4xl md:text-5xl font-medium mt-3 mb-5 leading-tight">
              {t("home.pros_title")}
            </h3>
            <p className="text-white/70 leading-relaxed mb-6 max-w-md">
              {t("home.pros_desc")}
            </p>
            <div>
              <Button
                onClick={() => navigate("/register")}
                className="rounded-full bg-[#D6202C] hover:bg-[#B5181F] text-white px-7 h-12"
              >
                {t("home.pros_cta")} <ArrowRight className="w-4 h-4 ms-2" />
              </Button>
            </div>
          </div>
          <div className="hidden md:block">
            <img
              src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=1200&q=85"
              alt="Agent"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}

const StatTile = ({ icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-[#E5E5E5] p-5">
    <div className="w-10 h-10 rounded-xl bg-[#FEF2F3] text-[#D6202C] flex items-center justify-center mb-3">
      {icon}
    </div>
    <div className="font-heading font-semibold text-2xl">{value}</div>
    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
  </div>
);
